/**
 * Gmail Inbox — connects one or more Gmail mailboxes so inbound email becomes
 * Velaro tickets or conversations. Supports individual OAuth and Domain-Wide
 * Delegation (DWD) for Google Workspace admins.
 *
 * OAuth flow:
 *   1. GET GmailOAuth/auth-url  → { authUrl }  (authenticated via Bearer token)
 *   2. window.location.href = authUrl          (browser goes to Google sign-in)
 *   3. Google → GmailOAuth/callback (messaging) → redirect back with ?gmailConnected=true
 */
import * as React from "react";
import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import useAdminFeatures from "@/hooks/useAdminFeatures";
import { PrimaryBlueButton, SecondaryBlueButton } from "@velaro/velaro-component-lib";
import IntegrationActivityLog from "../IntegrationActivityLog";
import StarterWorkflowWizard from "../StarterWorkflowWizard";

interface GmailMailbox {
  id: number;
  mailboxEmail: string;
  label: string | null;
  teamId: number | null;
  teamName: string | null;
  isEnabled: boolean;
  connectionType: string;       // "OAuth" | "DWD"
  connectedByEmail: string | null;
  dwdOrgDomain: string | null;
  watchExpiresAt: string | null;
}

interface ConfigStatus {
  oauthConfigured: boolean;
  dwdConfigured: boolean;
  pubSubConfigured: boolean;
}

const RETURN_URL = "/integrations/gmail-inbox";

export default function GmailInbox() {
  const api = useApi();
  const { teams } = useTeams();
  const { enableGmailDwd } = useAdminFeatures();
  const [searchParams, setSearchParams] = useSearchParams();

  const [mailboxes, setMailboxes] = useState<GmailMailbox[]>([]);
  const [config, setConfig] = useState<ConfigStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [showWizard, setShowWizard] = useState(false);

  // DWD add-mailbox form
  const [showDwdForm, setShowDwdForm] = useState(false);
  const [dwdEmail, setDwdEmail] = useState("");
  const [dwdTeamId, setDwdTeamId] = useState<number | null>(null);
  const [dwdSaving, setDwdSaving] = useState(false);
  const [dwdError, setDwdError] = useState("");
  const [dwdVerifyingId, setDwdVerifyingId] = useState<number | null>(null);
  const [dwdVerifyResults, setDwdVerifyResults] = useState<Record<number, { ok: boolean; message: string }>>({});

  const oauthConnected = searchParams.get("gmailConnected") === "true";
  const oauthError = searchParams.get("gmailError");

  useEffect(() => {
    if (oauthConnected || oauthError) {
      setSearchParams({}, { replace: true });
    }
  }, [oauthConnected, oauthError]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, cfgRes] = await Promise.all([
        api.messaging.get("GmailOAuth/list"),
        api.messaging.get("GmailOAuth/config-status"),
      ]);
      const [listData, cfgData] = await Promise.all([listRes.json(), cfgRes.json()]);
      setMailboxes(Array.isArray(listData) ? listData : []);
      setConfig(cfgData ?? null);
      setDwdVerifyResults({});
    } catch (ex) {
      console.error("GmailInbox: load failed", ex);
      setMailboxes([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  async function handleConnect() {
    setConnecting(true);
    setConnectError("");
    try {
      const res = await api.messaging.get(`GmailOAuth/auth-url?returnUrl=${encodeURIComponent(RETURN_URL)}`);
      const data = await res.json();
      if (!res.ok) {
        setConnectError(data?.error ?? "Failed to start Gmail connection.");
        setConnecting(false);
        return;
      }
      window.location.href = data.authUrl;
    } catch (ex) {
      setConnectError("Failed to start Gmail connection. Please try again.");
      console.error("GmailInbox: auth-url failed", ex);
      setConnecting(false);
    }
  }

  async function handleToggle(m: GmailMailbox) {
    setTogglingId(m.id);
    try {
      await api.messaging.patch(`GmailOAuth/${m.id}/toggle`, {});
      await load();
    } catch (ex) {
      console.error("GmailInbox: toggle failed", ex);
    } finally {
      setTogglingId(null);
    }
  }

  async function handleDisconnect(m: GmailMailbox) {
    if (!confirm(`Disconnect ${m.mailboxEmail}? Inbound emails to this mailbox will stop being processed.`)) return;
    setDeletingId(m.id);
    try {
      await api.messaging.delete(`GmailOAuth/${m.id}`);
      await load();
    } catch (ex) {
      console.error("GmailInbox: disconnect failed", ex);
    } finally {
      setDeletingId(null);
    }
  }

  async function handleAddDwd() {
    if (!dwdEmail.trim()) { setDwdError("Mailbox email is required."); return; }
    setDwdSaving(true);
    setDwdError("");
    try {
      const res = await api.messaging.post("GmailOAuth/dwd", {
        mailboxEmail: dwdEmail.trim(),
        teamId: dwdTeamId ?? null,
      });
      const data = await res.json();
      if (!res.ok) { setDwdError(data?.error ?? "Failed to add mailbox."); return; }
      setShowDwdForm(false);
      setDwdEmail("");
      setDwdTeamId(null);
      await load();
    } catch (ex) {
      console.error("GmailInbox: add DWD mailbox failed", ex);
      setDwdError("Failed to add mailbox. Please try again.");
    } finally {
      setDwdSaving(false);
    }
  }

  async function handleVerifyDwd(id: number) {
    setDwdVerifyingId(id);
    try {
      const res = await api.messaging.post(`GmailOAuth/dwd/${id}/verify`, {});
      const data = await res.json();
      setDwdVerifyResults(prev => ({ ...prev, [id]: data }));
    } catch (ex) {
      console.error("GmailInbox: DWD verify failed", ex);
      setDwdVerifyResults(prev => ({ ...prev, [id]: { ok: false, message: "Verification request failed." } }));
    } finally {
      setDwdVerifyingId(null);
    }
  }

  if (showWizard) {
    return <StarterWorkflowWizard integration="gmail-inbox" onClose={() => setShowWizard(false)} />;
  }

  return (
    <div className="p-6 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-white border border-slate-200 shadow-sm p-1">
            <svg viewBox="0 0 24 24" className="w-full h-full">
              <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.907 1.528-1.145C21.69 2.28 24 3.434 24 5.457z" fill="#EA4335"/>
            </svg>
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Gmail Inbox</h1>
            <p className="text-sm text-slate-500">
              Connect Gmail mailboxes so every inbound email becomes a ticket or conversation.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <SecondaryBlueButton label="Create Workflow" onClick={() => setShowWizard(true)} />
          {config?.oauthConfigured && (
            <PrimaryBlueButton
              label={connecting ? "Connecting..." : "Connect Gmail Account"}
              onClick={handleConnect}
              disabled={connecting}
            />
          )}
        </div>
      </div>

      {oauthConnected && (
        <div className="mb-4 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-800">
          Gmail account connected successfully. Inbound emails will now flow into your inbox.
        </div>
      )}
      {oauthError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Gmail connection failed: {oauthError}. Please try again.
        </div>
      )}
      {connectError && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {connectError}
        </div>
      )}

      {config && !config.oauthConfigured && !(enableGmailDwd && config.dwdConfigured) && (
        <div className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <p className="font-medium mb-1">Gmail not yet configured</p>
          <p>Contact Velaro support to enable Gmail integration — OAuth credentials and a Google Cloud Pub/Sub topic need to be set up for your account.</p>
        </div>
      )}

      <SetupInstructions config={config} showDwd={enableGmailDwd && !!config?.dwdConfigured} />

      <div className="mb-6">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Connected Mailboxes</h2>
        {loading ? (
          <div className="text-sm text-slate-400 py-4">Loading...</div>
        ) : mailboxes.length === 0 ? (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-center">
            <p className="text-sm font-medium text-slate-600 mb-1">No mailboxes connected yet</p>
            <p className="text-xs text-slate-400 mb-4">
              Connect a Gmail account to start receiving emails as tickets.
            </p>
            {config?.oauthConfigured && (
              <PrimaryBlueButton
                label={connecting ? "Connecting..." : "Connect Your First Mailbox"}
                onClick={handleConnect}
                disabled={connecting}
              />
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {mailboxes.map(m => (
              <MailboxRow
                key={m.id}
                mailbox={m}
                toggling={togglingId === m.id}
                deleting={deletingId === m.id}
                verifying={dwdVerifyingId === m.id}
                verifyResult={dwdVerifyResults[m.id]}
                onToggle={() => handleToggle(m)}
                onDisconnect={() => handleDisconnect(m)}
                onVerifyDwd={() => handleVerifyDwd(m.id)}
              />
            ))}
          </div>
        )}
      </div>

      {enableGmailDwd && config?.dwdConfigured && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-sm font-semibold text-slate-700">Domain-Wide Delegation</h2>
              <p className="text-xs text-slate-400 mt-0.5">
                For Google Workspace admins — add any mailbox in your org without per-user login.
              </p>
            </div>
            <SecondaryBlueButton label="Add DWD Mailbox" onClick={() => { setShowDwdForm(true); setDwdError(""); }} />
          </div>

          {showDwdForm && (
            <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  Mailbox email <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  placeholder="support@yourcompany.com"
                  value={dwdEmail}
                  onChange={e => setDwdEmail(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Assign to team (optional)</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  value={dwdTeamId ?? ""}
                  onChange={e => setDwdTeamId(e.target.value ? Number(e.target.value) : null)}
                >
                  <option value="">No team (use site default)</option>
                  {(teams ?? []).map(t => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>
              {dwdError && (
                <p className="text-xs text-red-600 bg-red-50 rounded px-3 py-2">{dwdError}</p>
              )}
              <div className="flex justify-end gap-3">
                <SecondaryBlueButton label="Cancel" onClick={() => setShowDwdForm(false)} disabled={dwdSaving} />
                <PrimaryBlueButton label={dwdSaving ? "Adding..." : "Add Mailbox"} onClick={handleAddDwd} disabled={dwdSaving} />
              </div>
            </div>
          )}
        </div>
      )}

      <div className="mt-8">
        <h2 className="text-sm font-semibold text-slate-700 mb-3">Activity Log</h2>
        <IntegrationActivityLog integrationSlug="GmailInbox" />
      </div>
    </div>
  );
}

// ── Mailbox row ─────────────────────────────────────────────────────────────

interface MailboxRowProps {
  mailbox: GmailMailbox;
  toggling: boolean;
  deleting: boolean;
  verifying: boolean;
  verifyResult?: { ok: boolean; message: string };
  onToggle: () => void;
  onDisconnect: () => void;
  onVerifyDwd: () => void;
}

function MailboxRow({ mailbox: m, toggling, deleting, verifying, verifyResult, onToggle, onDisconnect, onVerifyDwd }: MailboxRowProps) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`inline-flex h-2 w-2 rounded-full flex-shrink-0 ${m.isEnabled ? "bg-green-500" : "bg-slate-300"}`} />
            <span className="font-medium text-slate-800 text-sm truncate">{m.mailboxEmail}</span>
            <span className="text-xs bg-slate-100 text-slate-500 px-1.5 rounded">{m.connectionType}</span>
            {!m.isEnabled && <span className="text-xs text-slate-400 bg-slate-100 px-1.5 rounded">Disabled</span>}
          </div>
          {m.label && <p className="text-xs text-slate-500 mb-0.5">Label: {m.label}</p>}
          {m.teamName && <p className="text-xs text-slate-500 mb-0.5">Team: {m.teamName}</p>}
          {m.connectedByEmail && <p className="text-xs text-slate-400">Connected by {m.connectedByEmail}</p>}
          {verifyResult && (
            <p className={`mt-1 text-xs ${verifyResult.ok ? "text-green-700" : "text-red-600"}`}>
              {verifyResult.ok ? "✓ " : "✗ "}{verifyResult.message}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {m.connectionType === "DWD" && (
            <button className="text-xs text-blue-600 hover:underline" onClick={onVerifyDwd} disabled={verifying}>
              {verifying ? "Verifying..." : "Verify"}
            </button>
          )}
          <button className="text-xs text-slate-500 hover:underline" onClick={onToggle} disabled={toggling}>
            {toggling ? "..." : m.isEnabled ? "Disable" : "Enable"}
          </button>
          <button className="text-xs text-red-500 hover:underline" onClick={onDisconnect} disabled={deleting}>
            {deleting ? "..." : "Disconnect"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Setup instructions ───────────────────────────────────────────────────────

function SetupInstructions({ config, showDwd }: { config: ConfigStatus | null; showDwd: boolean }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white mb-5 overflow-hidden">
      <button
        type="button"
        className="w-full flex items-center justify-between px-5 py-3.5 text-sm font-medium text-slate-700 hover:bg-slate-50 text-left"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <span>How to connect Gmail to Velaro</span>
        <span className="text-slate-400 text-xs ml-2">{open ? "▲" : "▼"}</span>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-slate-100 text-sm text-slate-600 space-y-4">
          {config?.oauthConfigured && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-700 mb-2">Option A — Individual Gmail account (OAuth)</p>
              <ol className="space-y-2">
                {[
                  "Click Connect Gmail Account above.",
                  "Sign in with the Google account whose inbox you want to connect.",
                  "Grant the requested permissions (read, send, modify emails).",
                  "Velaro registers a push notification so new emails arrive in real time.",
                  "Assign the mailbox to a team in the mailbox row after connecting.",
                ].map((text, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-red-500 text-white text-xs font-bold mt-0.5">{i + 1}</span>
                    <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {showDwd && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-slate-700 mb-2">Option B — Domain-Wide Delegation (Google Workspace)</p>
              <ol className="space-y-2">
                {[
                  "Your IT admin must authorize the Velaro service account in Google Workspace Admin → Security → Domain-Wide Delegation.",
                  "Use the Add DWD Mailbox form to enter any mailbox in your organization.",
                  "Click Verify to confirm impersonation works for that mailbox.",
                  "No per-user OAuth flow needed — IT authorizes once for all mailboxes.",
                ].map((text, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="flex-shrink-0 flex items-center justify-center h-5 w-5 rounded-full bg-[var(--color-primary)] text-white text-xs font-bold mt-0.5">{i + 1}</span>
                    <p className="text-xs text-slate-600 leading-relaxed">{text}</p>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-4 py-3 text-xs text-blue-800">
            <strong>Email routing:</strong> Use routing rules in Ticket Settings to send emails from specific addresses to specific teams, or set mode to "ignore" for no-reply senders.
          </div>
        </div>
      )}
    </div>
  );
}
